import { useState, useEffect } from "react";
import axios from "axios";
import { PageLayout } from "../components/PageLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Mail,
  Phone,
  Globe,
  MapPin,
  Users,
  Building,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const API = `${window.location.origin}/api`;

const AdminAssociationsPage = () => {
  const [associations, setAssociations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [associationToDelete, setAssociationToDelete] = useState(null);
  const [editingAssociation, setEditingAssociation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedAssociation, setExpandedAssociation] = useState(null);
  const [associationDocenti, setAssociationDocenti] = useState({});

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    logo_url: "",
    website: "",
    email: "",
    phone: "",
    address: "",
  });

  useEffect(() => {
    fetchAssociations();
  }, []);

  const fetchAssociations = async () => {
    try {
      const response = await axios.get(`${API}/associations`);
      setAssociations(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento delle associazioni");
    } finally {
      setLoading(false);
    }
  };

  const fetchAssociationDocenti = async (associationId) => {
    try {
      const response = await axios.get(`${API}/associations/${associationId}/docenti`);
      setAssociationDocenti((prev) => ({
        ...prev,
        [associationId]: response.data,
      }));
    } catch (error) {
      console.error("Error fetching docenti:", error);
    }
  };

  const toggleExpand = (associationId) => {
    if (expandedAssociation === associationId) {
      setExpandedAssociation(null);
    } else {
      setExpandedAssociation(associationId);
      if (!associationDocenti[associationId]) {
        fetchAssociationDocenti(associationId);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      logo_url: "",
      website: "",
      email: "",
      phone: "",
      address: "",
    });
    setEditingAssociation(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (association) => {
    setEditingAssociation(association);
    setFormData({
      name: association.name,
      description: association.description || "",
      logo_url: association.logo_url || "",
      website: association.website || "",
      email: association.email || "",
      phone: association.phone || "",
      address: association.address || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingAssociation) {
        await axios.put(`${API}/admin/associations/${editingAssociation.association_id}`, formData);
        toast.success("Associazione aggiornata con successo");
      } else {
        await axios.post(`${API}/admin/associations`, formData);
        toast.success("Associazione creata con successo");
      }
      setDialogOpen(false);
      resetForm();
      fetchAssociations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!associationToDelete) return;

    try {
      await axios.delete(`${API}/admin/associations/${associationToDelete.association_id}`);
      toast.success("Associazione eliminata");
      fetchAssociations();
    } catch (error) {
      toast.error("Errore durante l'eliminazione");
    } finally {
      setDeleteDialogOpen(false);
      setAssociationToDelete(null);
    }
  };

  return (
    <PageLayout>
      

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mb-2">
              Gestione Associazioni
            </h1>
            <p className="text-muted-foreground">
              Gestisci le associazioni partner (CNA, ISPEO, OTI, ecc.)
            </p>
          </div>
          <Button
            onClick={openCreateDialog}
            className="rounded-full mt-4 md:mt-0"
            data-testid="create-association-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuova Associazione
          </Button>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-secondary rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : associations.length > 0 ? (
          <div className="space-y-4">
            {associations.map((association) => (
              <Card key={association.association_id} className="rounded-2xl border-border/50 overflow-hidden">
                <CardContent className="p-0">
                  <div className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      <Avatar className="w-16 h-16 border-2 border-primary/20 rounded-xl">
                        <AvatarImage src={association.logo_url} alt={association.name} />
                        <AvatarFallback className="bg-primary/10 text-primary font-serif text-lg rounded-xl">
                          {association.name.substring(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-serif text-xl font-medium">{association.name}</h3>
                          <Badge variant="secondary" className="rounded-full">
                            <Users className="w-3 h-3 mr-1" />
                            {association.docenti_count} docenti
                          </Badge>
                        </div>
                        {association.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                            {association.description}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                          {association.email && (
                            <div className="flex items-center gap-1">
                              <Mail className="w-4 h-4" />
                              <span>{association.email}</span>
                            </div>
                          )}
                          {association.phone && (
                            <div className="flex items-center gap-1">
                              <Phone className="w-4 h-4" />
                              <span>{association.phone}</span>
                            </div>
                          )}
                          {association.website && (
                            <a
                              href={association.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-primary hover:underline"
                            >
                              <Globe className="w-4 h-4" />
                              <span>Sito web</span>
                            </a>
                          )}
                          {association.address && (
                            <div className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              <span>{association.address}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full"
                          onClick={() => toggleExpand(association.association_id)}
                        >
                          {expandedAssociation === association.association_id ? (
                            <>
                              <ChevronUp className="w-4 h-4 mr-1" />
                              Nascondi Docenti
                            </>
                          ) : (
                            <>
                              <ChevronDown className="w-4 h-4 mr-1" />
                              Vedi Docenti
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full"
                          onClick={() => openEditDialog(association)}
                          data-testid={`edit-association-${association.association_id}`}
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Modifica
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="rounded-full text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => {
                            setAssociationToDelete(association);
                            setDeleteDialogOpen(true);
                          }}
                          data-testid={`delete-association-${association.association_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Docenti Section */}
                  {expandedAssociation === association.association_id && (
                    <div className="border-t border-border/50 bg-secondary/30 p-6">
                      <h4 className="font-medium mb-4 flex items-center gap-2">
                        <Users className="w-4 h-4 text-primary" />
                        Docenti Responsabili
                      </h4>
                      {associationDocenti[association.association_id]?.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {associationDocenti[association.association_id].map((docente) => (
                            <div
                              key={docente.docente_id}
                              className="flex items-center gap-3 p-3 bg-white rounded-xl"
                            >
                              <Avatar className="w-10 h-10">
                                <AvatarImage src={docente.photo_url} alt={docente.name} />
                                <AvatarFallback className="bg-primary/10 text-primary text-sm">
                                  {docente.name.split(" ").map((n) => n[0]).join("")}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{docente.name}</p>
                                <p className="text-xs text-muted-foreground truncate">{docente.phone}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">Nessun docente associato</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="rounded-2xl border-border/50">
            <CardContent className="py-16 text-center">
              <Building className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground mb-4">Nessuna associazione registrata</p>
              <Button onClick={openCreateDialog} className="rounded-full">
                Aggiungi la prima associazione
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {editingAssociation ? "Modifica Associazione" : "Nuova Associazione"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome Associazione *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="es. CNA, ISPEO, OTI"
                className="rounded-xl"
                data-testid="association-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrizione</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                placeholder="Descrizione dell'associazione..."
                className="rounded-xl"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="info@associazione.it"
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Telefono</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+39 06 1234567"
                  className="rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="website">Sito Web</Label>
              <Input
                id="website"
                name="website"
                type="url"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://www.esempio.it"
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Indirizzo</Label>
              <Input
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Via Roma 1, 00100 Roma"
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="logo_url">URL Logo</Label>
              <Input
                id="logo_url"
                name="logo_url"
                type="url"
                value={formData.logo_url}
                onChange={handleChange}
                placeholder="https://..."
                className="rounded-xl"
              />
              {formData.logo_url && (
                <div className="mt-2">
                  <Avatar className="w-20 h-20 rounded-xl">
                    <AvatarImage src={formData.logo_url} alt="Anteprima logo" />
                    <AvatarFallback className="rounded-xl">Logo</AvatarFallback>
                  </Avatar>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                className="rounded-full"
                onClick={() => setDialogOpen(false)}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                className="rounded-full"
                disabled={saving}
                data-testid="save-association-btn"
              >
                {saving ? "Salvataggio..." : editingAssociation ? "Salva Modifiche" : "Crea Associazione"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina associazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare "{associationToDelete?.name}"?
              I docenti associati non verranno eliminati, ma perderanno l'associazione.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageLayout>
  );
};

export default AdminAssociationsPage;
